import { ColourPicker } from '@components/core-ui/ColourPicker';
import { Input } from '@components/core-ui/form-elements/Input';
import { SlugField } from '@components/core-ui/form-elements/SlugField';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { EntityIconMd } from '@components/core-ui/EntityIcon/variations/EntityIconMd';
import { useSlugAvailability } from '@helpers/useSlugAvailability';
import { useAPI } from '@providers/useAPI';
import QuestionIcon from '@components/icons/QuestionIcon18.svg';
import { useAuthContext } from '@providers/AuthProvider';
import { FindOneTeamResponseDto, TeamsService, UpdateTeamBodyDto } from 'app/API';
import { EVIEW_JOBS_PERMISSION } from 'app/types/enums/EVIEW_JOBS_PERMISSION';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFetchTeamRenameSlugAvailabilityQueryFn } from 'app/queries/teams/createFetchTeamRenameSlugAvailabilityQueryFn';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import React, { useState } from 'react';
import { ErrorMessage } from './styles/ErrorMessage';
import { InputBlockWrapper } from './styles/InputBlockWrapper';
import { InputWrapper } from './styles/InputWrapper';
import { LeftSide } from './styles/LeftSide';
import { PermissionLabel } from './styles/PermissionLabel';
import { QuestionContent } from './styles/QuestionContent';
import { QuestionIconWrapper } from './styles/QuestionIconWrapper';
import { RightSide } from './styles/RightSide';
import { RightSideColumns } from './styles/RightSideColumns';
import { Select } from '@components/core-ui/form-elements/Select';
import { Wrapper } from './styles/Wrapper';
import { Button } from '@components/core-ui/form-elements/buttons/Button';

const toggleOrgPermissionsGroups: (keyof UpdateTeamBodyDto)[][] = [
  ['manageOrganization', 'manageWorkspaces', 'manageMembers', 'managePermissions'],

  ['manageBilling', 'viewBilling', 'manageLicenses', 'viewLicenses'],

  ['viewReports', 'editJobs', 'submitJobs', 'reassignJobs', 'deleteJobs'],

  ['editRepositories', 'viewRepositories', 'viewJobCost'],
];

const toggleWSPermissionsGroups: (keyof UpdateTeamBodyDto)[][] = [
  ['editJobTranscript', 'proofreadJobTranscript'],
  ['finalizeJobTranscript', 'markJobComplete'],
];

const camelCaseToTitleCase = (text: string) => {
  const result = text.replace(/([A-Z])/g, ' $1').replace('Repositories', 'Cases');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const getInitialValues = (team: FindOneTeamResponseDto): UpdateTeamBodyDto => {
  return {
    name: team.name,
    slug: team.slug,
    colour: team.colour,

    viewRepositories: team.viewRepositories,
    editRepositories: team.editRepositories,

    viewBilling: team.viewBilling,
    manageBilling: team.manageBilling,

    viewLicenses: team.viewLicenses,
    manageLicenses: team.manageLicenses,

    viewJobs: team.viewJobs as UpdateTeamBodyDto['viewJobs'],
    editJobs: team.editJobs,
    reassignJobs: team.reassignJobs,
    deleteJobs: team.deleteJobs,
    submitJobs: team.submitJobs,
    viewJobCost: team.viewJobCost,

    editJobTranscript: team.editJobTranscript,
    proofreadJobTranscript: team.proofreadJobTranscript,
    finalizeJobTranscript: team.finalizeJobTranscript,
    markJobComplete: team.markJobComplete,

    viewReports: team.viewReports,

    manageMembers: team.manageMembers,
    managePermissions: team.managePermissions,
    manageWorkspaces: team.manageWorkspaces,

    manageOrganization: team.manageOrganization,
  };
};

const SAVE_ERROR = 'Unable to save this team.';

type TProps = {
  team: FindOneTeamResponseDto;
  onSaved: (savedSlug: string) => void;
};

export const TeamInformation = ({ team, onSaved }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<UpdateTeamBodyDto>(getInitialValues(team));

  const organizationId = organization?.id ?? '';
  const {
    status: slugStatus,
    isAvailable: slugIsAvailable,
    isUnverified: isSlugUnverified,
    retry: retrySlugCheck,
  } = useSlugAvailability({
    slug: form.slug ?? '',
    currentSlug: team.slug,
    enabled: !!organizationId,
    checkAvailability: async (slug) => {
      const result = await queryClient.fetchQuery({
        queryKey: teamsKeys.renameSlugAvailability(organizationId, team.id, slug),
        queryFn: createFetchTeamRenameSlugAvailabilityQueryFn(call),
        staleTime: 0,
      });
      return !!result?.available;
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: (fields: UpdateTeamBodyDto) =>
      call(() => TeamsService.update({ organizationRoleId: team.id, requestBody: fields }), {
        onError: (message) => setError(message || SAVE_ERROR),
      }),
    onMutate: () => setError(null),
    onError: () => setError((current) => current ?? SAVE_ERROR),
    onSuccess: (_data, fields) => onSaved(fields.slug ?? team.slug),
  });

  const isSubmitting = updateTeamMutation.isPending;

  return (
    <>
      {isSubmitting && <AbsoluteSpinner overlay={true} />}
      <Wrapper>
        <LeftSide>
          <h3>General</h3>
        </LeftSide>

        <RightSide>
          <RightSideColumns>
            <EntityIconMd
              name={form.name || form.slug || ''}
              backgroundColor={form.colour}
              color="var(--color-surface)"
            />
            {!team.default ? (
              <div>
                <ColourPicker
                  disabled={team.default}
                  colour={form.colour}
                  setColour={(colour) => setForm({ ...form, colour })}
                />
                <p className="text-xs">This icon color will be used to identify your team.</p>
              </div>
            ) : null}
          </RightSideColumns>
          <RightSideColumns>
            <div className="team-name">
              <InputWrapper>
                <label htmlFor="name">Team name</label>
                <Input
                  className="form-input w-full"
                  disabled={isSubmitting || team.default}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                  name="name"
                  value={form.name}
                  label="Team name"
                />
              </InputWrapper>
              <InputWrapper>
                <label htmlFor="slug">Team URL</label>
                <SlugField
                  className="w-full"
                  label="Team URL"
                  value={form.slug ?? ''}
                  status={slugStatus}
                  onRetry={retrySlugCheck}
                  disabled={team.default}
                  onChange={(value) => setForm({ ...form, slug: value })}
                />
              </InputWrapper>
            </div>
          </RightSideColumns>
        </RightSide>
      </Wrapper>
      <Wrapper>
        <LeftSide>
          <h3>Permissions</h3>
        </LeftSide>
        <RightSide>
          <RightSideColumns>
            <div className="view-jobs">
              <InputBlockWrapper className="view-jobs-field">
                <InputWrapper>
                  <label htmlFor="viewJobs">View jobs</label>
                  <Select
                    name="viewJobs"
                    className="form-select min-w-0 w-full"
                    disabled={team.default}
                    label=""
                    value={form.viewJobs}
                    onChange={(_n, value) => setForm({ ...form, viewJobs: value as EVIEW_JOBS_PERMISSION })}
                    options={[
                      { value: 'assigned', label: 'Assigned' },
                      { value: 'assignedAndUnassigned', label: 'Assigned & Unassigned' },
                      { value: 'all', label: 'All' },
                      { value: 'none', label: 'None' },
                    ]}
                  />
                </InputWrapper>
              </InputBlockWrapper>
              <div className="view-jobs-hint">
                <QuestionIconWrapper className="absolute left-0 top-9">
                  <QuestionIcon />
                  <QuestionContent className="question-content">
                    Choose the type of job list that fits this team&apos;s needs:
                    <ul>
                      <li>All - view all jobs within an attached Workspace</li>
                      <li>Assigned - only view jobs which have been assigned to the current user</li>
                      <li>
                        Assigned and Unassigned - view jobs which have been assigned to the current user and jobs which
                        are available to be claimed (with the Edit Job Transcript or Proofread Job Transcript
                        Permissions)
                      </li>
                      <li>None - disable the job list.</li>
                    </ul>
                  </QuestionContent>
                </QuestionIconWrapper>
              </div>
            </div>
          </RightSideColumns>

          <PermissionLabel>Organization level</PermissionLabel>
          <div className="flex mt-4 gap-16">
            {toggleOrgPermissionsGroups.map((group, i) => (
              <div key={i} className="mr-4 min-w-12">
                {group.map((permission, ii) => (
                  <div key={`${i}-${ii}`} className="flex mb-2">
                    <label htmlFor={permission} className="mr-4 whitespace-nowrap">
                      {camelCaseToTitleCase(permission)}
                    </label>
                    <div className="flex-grow grid justify-items-end">
                      <Switch
                        disabled={team.default}
                        checked={form[permission] as boolean}
                        onChange={() => {
                          setForm({ ...form, [permission]: !form[permission] });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <PermissionLabel>Workspace level</PermissionLabel>
          <div className="flex mt-4 gap-16">
            {toggleWSPermissionsGroups.map((group, i) => (
              <div key={i} className="mr-4 min-w-12">
                {group.map((permission, ii) => (
                  <div key={`${i}-${ii}`} className="flex mb-2">
                    <label htmlFor={permission} className="mr-4 whitespace-nowrap">
                      {camelCaseToTitleCase(permission)}
                    </label>
                    <div className="flex-grow grid justify-items-end">
                      <Switch
                        disabled={team.default}
                        checked={form[permission] as boolean}
                        onChange={() => {
                          setForm({ ...form, [permission]: !form[permission] });
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </RightSide>
      </Wrapper>

      <div className="grid place-items-end">
        {!team.default ? (
          <div className="w-36">
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button
              className="my-2 w-[140px]"
              disabled={isSubmitting || isSlugUnverified || !slugIsAvailable}
              onClick={() => updateTeamMutation.mutate(form)}
            >
              Save
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
};
